import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, FileText, ArrowRight, ShoppingBag, Sparkles, Star, TrendingUp, Download, Loader2 } from "lucide-react";

export default function Dashboard() {
  const [products, setProducts] = useState([]);
  const [docCount, setDocCount] = useState(0);
  const [quick, setQuick] = useState({ bookmarks: [], most_used: [] });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/my/purchases"), api.get("/my/documents"), api.get("/portal/quick-access")])
      .then(([p, d, q]) => { setProducts(p.data); setDocCount(d.data.length); setQuick(q.data); })
      .finally(() => setLoading(false));
  }, []);

  const download = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.title; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
    finally { setDownloading(null); }
  };

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Welcome back</h1>
      <p className="text-muted-foreground mt-2">Here's everything you have access to.</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {[
          [ShoppingBag, "Products owned", products.length],
          [FileText, "Documents available", docCount],
          [Sparkles, "Updates", "Included"],
        ].map(([Icon, label, val]) => (
          <div key={label} className="bg-white border border-border p-6">
            <Icon className="w-5 h-5 text-primary mb-4" />
            <div className="font-heading font-black text-3xl">{loading ? "—" : val}</div>
            <div className="text-sm text-muted-foreground mt-1">{label}</div>
          </div>
        ))}
      </div>

      {!loading && (quick.bookmarks.length > 0 || quick.most_used.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6 mt-10" data-testid="quick-access">
          <QuickList title="Quick Access" subtitle="Your bookmarked forms" icon={Star}
            items={quick.bookmarks} empty="Star a form in your library to pin it here."
            onDownload={download} downloading={downloading} testid="quick-bookmarks" />
          <QuickList title="Most Used" subtitle="Auto-updates as you work" icon={TrendingUp}
            items={quick.most_used} empty="Your most-opened forms will show up here."
            onDownload={download} downloading={downloading} testid="quick-mostused" showOpens />
        </div>
      )}

      <div className="flex items-center justify-between mt-12 mb-5">
        <h2 className="font-heading font-bold text-xl">Your products</h2>
        <Link to="/portal/library" className="text-sm font-semibold text-primary inline-flex items-center gap-1">
          Open library <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {!loading && products.length === 0 ? (
        <div className="bg-white border border-border p-10 text-center">
          <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-bold text-lg">No purchases yet</h3>
          <p className="text-muted-foreground mt-2">Browse the products to start building your operating system.</p>
          <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold mt-6 btn-lift">
            Explore products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} className="bg-white border border-border p-6" data-testid={`portal-product-${p.id}`}>
              <span className="text-[11px] uppercase tracking-wider font-semibold text-primary">Owned</span>
              <h3 className="font-heading font-bold text-lg mt-2">{p.short_name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>
              <Link to="/portal/library" className="text-sm font-semibold text-primary inline-flex items-center gap-1 mt-4">
                View documents <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickList({ title, subtitle, icon: Icon, items, empty, onDownload, downloading, testid, showOpens }) {
  return (
    <div className="bg-white border border-border p-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-primary" />
        <div>
          <h3 className="font-heading font-bold text-lg leading-tight">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4">{empty}</p>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {items.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-2.5" data-testid={`${testid}-item-${d.id}`}>
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{d.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {d.code && <span className="font-mono mr-2">{d.code}</span>}
                  {showOpens && d.opens > 0 && <span>{d.opens} open{d.opens === 1 ? "" : "s"}</span>}
                </div>
              </div>
              <button onClick={() => onDownload(d)} disabled={downloading === d.id}
                data-testid={`${testid}-download-${d.id}`}
                className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-primary border border-border px-3 py-2 hover:border-primary disabled:opacity-50">
                {downloading === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
